import { PaginatedResponse, Timesheet, TimeEntry } from '@/types'
import { apiRequestWithAutoRefresh } from '@/utils/apiUtils'

export interface WeeklyStats {
  weeklyTotalHours: number
  monthlyTotalHours: number
  averageQualityScore: number | null
  pendingSubmissions: number
  projectsInvolved: string[]
  weekStart: string
  weekEnd: string
  workingDays: number
}

export interface CurrentWeekEntries {
  weekStart: string
  weekEnd: string
  entries: TimeEntry[]
  entriesByDate: Record<string, TimeEntry[]>
  totalHours: number
  workingDays: number
  projectsInvolved: string[]
}

export interface WeekLockStatus {
  isLocked: boolean
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | null
  reason: string | null
  timesheetId: string | null
  weekStart: string
  weekEnd: string
  rejectionReason?: string
  revisionDeadline?: string
}

export interface SubmitTimesheetRequest {
  summary?: string
}

export interface UnsubmittedWeek {
  weekStart: string
  weekEnd: string
  totalHours: number
  workingDays: number
  overdueDays: number
  entriesCount: number
  entries: TimeEntry[]
}

export const timesheetService = {
  // 獲取工時申報列表
  async getTimesheets(params?: {
    page?: number
    limit?: number
    status?: string
  }): Promise<PaginatedResponse<Timesheet>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.status) searchParams.append('status', params.status)

    const result = await apiRequestWithAutoRefresh(`/timesheets?${searchParams.toString()}`, {
      method: 'GET'
    })

    return {
      data: result.data || [],
      pagination: (result as any).pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    }
  },

  // 獲取本週統計資料
  async getWeeklyStats(): Promise<WeeklyStats> {
    const result = await apiRequestWithAutoRefresh('/timesheets/weekly-stats', {
      method: 'GET'
    })
    return result.data
  },

  // 獲取本週工時明細
  async getCurrentWeekEntries(): Promise<CurrentWeekEntries> {
    const result = await apiRequestWithAutoRefresh('/timesheets/current-week-entries', {
      method: 'GET'
    })
    return result.data
  },

  // 提交本週工時申報
  async submitCurrentWeek(data: SubmitTimesheetRequest): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh('/timesheets/submit-current-week', {
      method: 'POST',
      body: JSON.stringify(data)
    })
    return result.data
  },

  // 獲取特定工時申報詳情
  async getTimesheet(id: string): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}`, {
      method: 'GET'
    })
    return result.data
  },

  // 獲取工時申報詳細資料（包含分析數據）
  async getTimesheetDetail(id: string): Promise<any> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}/detail`, {
      method: 'GET'
    })
    return result.data
  },

  // 管理者獲取待審核的工時申報
  async getPendingTimesheets(params?: {
    page?: number
    limit?: number
    status?: string
  }): Promise<PaginatedResponse<Timesheet>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.limit) searchParams.append('limit', params.limit.toString())
    if (params?.status) searchParams.append('status', params.status)

    const result = await apiRequestWithAutoRefresh(`/timesheets/admin/pending?${searchParams.toString()}`, {
      method: 'GET'
    })

    return {
      data: result.data || [],
      pagination: (result as any).pagination || {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      }
    }
  },

  // 管理者審核工時申報
  async reviewTimesheet(
    id: string,
    data: {
      action: 'approve' | 'reject'
      comments?: string
      qualityScore?: number
    }
  ): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}/review`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
    return result.data
  },

  // 檢查週次鎖定狀態
  async checkWeekLock(date: string): Promise<WeekLockStatus> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/check-week-lock/${date}`, {
      method: 'GET'
    })
    return result.data
  },

  // 撤回工時申報
  async withdrawTimesheet(id: string): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}/withdraw`, {
      method: 'POST'
    })
    return result.data
  },

  // 獲取可編輯的工時申報詳情
  async getEditableTimesheet(id: string): Promise<any> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}/edit`, {
      method: 'GET'
    })
    return result.data
  },

  // 更新工時申報
  async updateTimesheet(
    id: string,
    data: {
      entries: any[]
      comments?: string
    }
  ): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
    return result.data
  },

  // 提交已編輯的工時申報
  async submitEditedTimesheet(
    id: string,
    data: { summary?: string }
  ): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/${id}/submit`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
    return result.data
  },

  // 獲取未申報週次
  async getUnsubmittedWeeks(): Promise<UnsubmittedWeek[]> {
    const result = await apiRequestWithAutoRefresh('/timesheets/unsubmitted-weeks', {
      method: 'GET'
    })
    return result.data
  },

  // 為指定週次創建申報草稿
  async createDraftTimesheet(weekStart: string): Promise<Timesheet> {
    const result = await apiRequestWithAutoRefresh(`/timesheets/create-draft/${weekStart}`, {
      method: 'POST'
    })
    return result.data
  }
}

// 工具函數
export const timesheetUtils = {
  // 格式化週次範圍
  formatWeekRange: (weekStart: string, weekEnd: string): string => {
    const start = new Date(weekStart)
    const end = new Date(weekEnd)
    
    const formatDate = (date: Date) => {
      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`
    }
    
    return `${formatDate(start)} - ${formatDate(end)}`
  },

  // 獲取狀態顯示文字
  getStatusText: (status: string): string => {
    const statusMap: Record<string, string> = {
      'DRAFT': '草稿',
      'SUBMITTED': '審核中',
      'APPROVED': '已核准',
      'REJECTED': '已駁回'
    }
    return statusMap[status] || status
  },

  // 獲取狀態顏色
  getStatusColor: (status: string): string => {
    const colorMap: Record<string, string> = {
      'DRAFT': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'SUBMITTED': 'text-blue-600 bg-blue-50 border-blue-200',
      'APPROVED': 'text-green-600 bg-green-50 border-green-200',
      'REJECTED': 'text-red-600 bg-red-50 border-red-200'
    }
    return colorMap[status] || 'text-gray-600 bg-gray-50 border-gray-200'
  },

  // 獲取品質分數等級
  getQualityLevel: (score: number | null): { text: string; color: string } => {
    if (score === null) return { text: '尚未評分', color: 'text-gray-500' }
    
    if (score >= 90) return { text: '傑出', color: 'text-green-600' }
    if (score >= 80) return { text: '符合預期', color: 'text-blue-600' }
    if (score >= 70) return { text: '需要改善', color: 'text-yellow-600' }
    return { text: '不符合標準', color: 'text-red-600' }
  },

  // 將秒數轉換為小時數，精確到小數點後兩位（與後端一致）
  convertSecondsToHours: (seconds: number): number => {
    if (seconds <= 0) return 0
    return Math.round((seconds / 3600) * 100) / 100
  },

  // 計算薪資金額，基於精確工時
  calculateSalary: (seconds: number, hourlyRate: number): number => {
    const hours = timesheetUtils.convertSecondsToHours(seconds)
    return Math.round(hours * hourlyRate)
  },

  // 格式化工時顯示（精確到小數點後兩位）
  formatHours: (hours: number): string => {
    return `${hours.toFixed(2)}h`
  },

  // 格式化秒數為工時顯示
  formatSecondsToHours: (seconds: number): string => {
    const hours = timesheetUtils.convertSecondsToHours(seconds)
    return timesheetUtils.formatHours(hours)
  },

  // 按日期分組時間條目
  groupEntriesByDate: (entries: TimeEntry[]): Record<string, TimeEntry[]> => {
    return entries.reduce((acc, entry) => {
      const date = entry.startTime.split('T')[0]
      if (!acc[date]) {
        acc[date] = []
      }
      acc[date].push(entry)
      return acc
    }, {} as Record<string, TimeEntry[]>)
  },

  // 計算日期的總工時（使用精確計算）
  calculateDayTotalHours: (entries: TimeEntry[]): number => {
    const totalSeconds = entries.reduce((sum, entry) => sum + entry.duration, 0)
    return timesheetUtils.convertSecondsToHours(totalSeconds)
  },

  // 獲取日期的中文星期
  getChineseWeekday: (dateString: string): string => {
    const date = new Date(dateString)
    const weekdays = ['日', '一', '二', '三', '四', '五', '六']
    return weekdays[date.getDay()]
  },

  // 格式化日期顯示
  formatDateDisplay: (dateString: string): string => {
    const date = new Date(dateString)
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const weekday = timesheetUtils.getChineseWeekday(dateString)
    
    return `${month}/${day} (${weekday})`
  }
}

export default timesheetService
